// Sprint 5.7 (D29) — inline rename state machine.
//
// Extracted from ChatHeader + AgentListItem so both surfaces share the same
// commit/cancel semantics:
//
//   begin()          → enter edit mode, prime `value` with `initial`
//   setValue(v)      → controlled typing
//   commit()         → trim + compare; no-op if empty or unchanged; otherwise
//                      call onCommit and close the editor on success. If
//                      onCommit throws, the editor stays open so the user
//                      can retry.
//   cancel()         → abandon the edit, restore `value` to `initial`.
//
// The caller drives the editing surface (an <input>), so the hook is UI-
// framework-agnostic beyond useState. One shared hook = one set of edge
// cases handled once (empty name, unchanged name, failed patch).

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseInlineRenameOpts {
  initial: string;
  onCommit: (nextName: string) => Promise<void> | void;
}

export interface InlineRenameState {
  editing: boolean;
  value: string;
  busy: boolean;
  begin(): void;
  cancel(): void;
  commit(): Promise<void>;
  setValue(next: string): void;
}

export function useInlineRename(opts: UseInlineRenameOpts): InlineRenameState {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(opts.initial);
  const [busy, setBusy] = useState(false);
  // Track whether commit() has already started for this edit cycle so that
  // a blur + Enter race (blur fires microseconds after Enter) doesn't
  // double-invoke onCommit.
  const committingRef = useRef(false);

  // Keep the value in sync if the upstream name changes while not editing —
  // e.g. auto-rename lands via WebSocket while the user hasn't touched the
  // field. Inside an active edit we never overwrite — that would drop the
  // user's in-progress typing.
  useEffect(() => {
    if (!editing) setValue(opts.initial);
  }, [opts.initial, editing]);

  const begin = useCallback(() => {
    committingRef.current = false;
    setValue(opts.initial);
    setEditing(true);
  }, [opts.initial]);

  const cancel = useCallback(() => {
    committingRef.current = false;
    setEditing(false);
    setValue(opts.initial);
  }, [opts.initial]);

  const commit = useCallback(async () => {
    if (committingRef.current) return;
    committingRef.current = true;
    const next = value.trim();
    if (!next || next === opts.initial) {
      setEditing(false);
      setValue(opts.initial);
      committingRef.current = false;
      return;
    }
    setBusy(true);
    try {
      await opts.onCommit(next);
      setEditing(false);
    } catch {
      // Leave the editor open with the user's text intact so they can retry.
      committingRef.current = false;
    } finally {
      setBusy(false);
    }
  }, [value, opts]);

  return { editing, value, busy, begin, cancel, commit, setValue };
}
